import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";
import {
  AXIS_TICK,
  BAR_RADIUS,
  CHART_GRID,
  CHART_MARGIN,
  CHART_PRIMARY,
  CHART_WARNING,
  MAX_BAR_SIZE,
  TICK_MARGIN,
  Y_AXIS_WIDTH_PCT,
  formatPercent,
} from "@/features/reports/components/chartTheme";
import { ChartTooltip } from "@/features/reports/components/ChartTooltip";
import type { DriverReportRow } from "@/types/reports";

interface Props {
  rows: DriverReportRow[];
}

/**
 * On-time % per driver bar chart, shown above the driver scorecard table
 * (TASK-3729). Domain fixed to [0, 100]; drivers with a null on-time % are
 * excluded from the chart.
 *
 * Styling adopts the shared chart theme (TASK-3835/3836): brand palette, shared
 * tooltip, value labels, and a percent-sized Y-axis width so "100%" fits and
 * tick digits are never clipped.
 *
 * The Y domain is data-driven (TASK-3838): real on-time values cluster at
 * 89–100%, so a fixed `[0, 100]` domain crushes every bar to full height and
 * hides the differences that matter. The lower bound floors at `min(80, dataMin
 * - 5)` (never below 0) and the upper bound is capped at 100, so the spread
 * between drivers is legible. An SLA reference line at 95% gives a benchmark.
 */
export function DriversOnTimeChart({ rows }: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const data = rows
    .filter((r) => r.on_time_percent != null)
    .map((r) => ({
      name: r.driver_name,
      onTime: r.on_time_percent as number,
    }));

  if (data.length === 0) {
    return null;
  }

  // Data-driven lower bound so 89–100% values stay distinguishable. Floor at
  // most 80, never below 0; the upper bound is always the 100% ceiling.
  const minValue = Math.min(...data.map((d) => d.onTime));
  const lowerBound = Math.max(0, Math.min(80, minValue - 5));
  const domain: [number, number] = [lowerBound, 100];

  const onTimeLabel = t("reports.driversTable.onTime");
  const slaLabel = t("reports.driversChart.slaTarget");

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {t("reports.driversChart.title")}
      </p>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_GRID}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              interval={0}
            />
            <YAxis
              domain={domain}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={Y_AXIS_WIDTH_PCT}
              tickMargin={TICK_MARGIN}
              tickFormatter={(value: number) => `${value}%`}
            />
            <Tooltip
              cursor={{ fill: CHART_GRID }}
              content={
                <ChartTooltip
                  valueFormatter={(value: ValueType) =>
                    formatPercent(
                      typeof value === "number" ? value : Number(value),
                      { language },
                    )
                  }
                />
              }
            />
            <ReferenceLine
              y={95}
              stroke={CHART_WARNING}
              strokeDasharray="4 3"
              label={{
                value: slaLabel,
                position: "insideTopRight",
                fill: CHART_WARNING,
                fontSize: 10,
              }}
            />
            <Bar
              dataKey="onTime"
              name={onTimeLabel}
              fill={CHART_PRIMARY}
              radius={BAR_RADIUS}
              maxBarSize={MAX_BAR_SIZE}
            >
              <LabelList
                dataKey="onTime"
                position="top"
                formatter={(value: number) =>
                  formatPercent(value, { language })
                }
                className="fill-gray-500 text-[10px]"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
